import React, { useId } from 'react';

import { Box, CircularProgress, Dialog } from '@mui/material';

import { Button } from '@/components/NewUI/ComponentsLibrary';

import { classes } from './styles';
import { GREY } from '../../../ThemeContext/constants';

import type { CSSProperties, Dispatch, ReactElement, SetStateAction } from 'react';

export interface IGenericDialogProps {
  alternateColor?: boolean;
  ariaLabel?: string;
  buttonCallback: (url: string, id: number) => void;
  buttonText: string;
  callbackLoading: boolean;
  description: string | ReactElement;
  dialogId?: number;
  disablePrimaryButton?: boolean;
  isDialogOpen: boolean;
  onCancelCallback?: () => void;
  overrideColor?: { primary: CSSProperties; secondary: CSSProperties };
  primaryButtonId?: string;
  secondaryButtonId?: string;
  secondaryButtonText?: string;
  setDialogOpen: Dispatch<SetStateAction<boolean>> | ((value: boolean) => void);
  switchOrder?: boolean;
  title: string;
  url: string;
}

const GenericDialogDescription = ({
  description
}: {
  description: string | ReactElement;
}): ReactElement => {
  if (!description) return <div />;
  if (typeof description !== 'string') {
    return <Box sx={{ width: '100%' }}>{description}</Box>;
  }

  return (
    <>
      {description.split('<br/>').map((line, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <span key={index}>{line}</span>
      ))}
    </>
  );
};

const GenericDialog = ({
  alternateColor,
  ariaLabel,
  buttonCallback,
  buttonText,
  callbackLoading,
  description,
  dialogId,
  disablePrimaryButton,
  isDialogOpen,
  onCancelCallback,
  overrideColor,
  primaryButtonId,
  secondaryButtonId,
  secondaryButtonText,
  setDialogOpen,
  switchOrder,
  title,
  url
}: IGenericDialogProps) => {
  const titleId = useId();

  if (process.env.NODE_ENV !== 'production' && !ariaLabel && !title?.trim()) {
    // eslint-disable-next-line no-console
    console.error(
      'GenericDialog requires a non-empty `title` or an `ariaLabel` prop so the dialog has an accessible name.'
    );
  }

  return (
    <Dialog
      aria-labelledby={titleId}
      onClose={() => setDialogOpen(false)}
      open={isDialogOpen}
      sx={{ '& .MuiPaper-root': { ...classes.dialog, maxWidth: '500px' }, zIndex: '20000' }}
      slotProps={
        ariaLabel ? { paper: { 'aria-label': ariaLabel, 'aria-labelledby': undefined } } : undefined
      }
    >
      <h4 id={titleId}>{title}</h4>
      <GenericDialogDescription description={description} />
      <Box
        sx={{
          ...classes.dialogButtonContainer,
          flexDirection: switchOrder ? 'row-reverse' : 'row'
        }}
      >
        <Button
          disableElevation
          buttonVariant="secondary"
          id={secondaryButtonId}
          style={{ padding: '10px 30px' }}
          onClick={() => {
            onCancelCallback?.();
            setDialogOpen(false);
          }}
          sx={{
            ...(alternateColor && classes.secondaryButtonAlternateColor),
            ...overrideColor?.secondary
          }}
        >
          {secondaryButtonText ?? 'Cancel'}
        </Button>
        <Button
          disableElevation
          buttonColor={alternateColor ? 'base' : 'red'}
          className="dialog-action-button"
          data-testid="dialog-action-button"
          disabled={disablePrimaryButton}
          id={primaryButtonId}
          onClick={() => (dialogId ? buttonCallback(url, dialogId) : buttonCallback(url, 0))}
          style={{ padding: '10px 30px' }}
          sx={{
            ...(alternateColor && classes.primaryButtonAlternateColor),
            '&.Mui-disabled': { background: '#E5E5E5', color: GREY },
            ...overrideColor?.primary
          }}
        >
          {callbackLoading ? <CircularProgress color="inherit" size={20} /> : buttonText}
        </Button>
      </Box>
    </Dialog>
  );
};

export default GenericDialog;
